import { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { NumericFormat } from "react-number-format";
import TaskActuals from "./TaskActuals";
import TaskAnalysis from "./TaskAnalysis";
import TaskBaselines from "./TaskBaselines";
import TaskDistributionPicker from "./TaskDistributionPicker";
import ForecastMethodPicker from "./ForecastMethodPicker";
import { Paper, TextField, ButtonGroup, Button, Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { runInAction } from "mobx";
import { useViewState } from "../model/ViewState";
import {GetPackageFAC} from "../operations/TaskPackageOperations"

const View = ({ task, tab }) => {
  if (tab === "analysis") {
    return <TaskAnalysis task={task} />;
  } else if (tab === "baseline") {
    return <TaskBaselines task={task} readOnly={true} />;
  } else {
    return <TaskActuals task={task} readOnly={true} />;
  }
};

const TaskPackagePanel = ({ selectedTask }) => {
  const view = useViewState();
  const [FACValues, setFACValues] = useState({None: 0});

  const handleChangeWeightAllocation = (event, newValue) => {
    selectedTask.costWeightAllocation = newValue.toString();
    selectedTask.durationWeightAllocation = (100 - newValue).toString();
  }
    useEffect(() => {
      GetPackageFAC(selectedTask.id, 
        (result => {
          setFACValues(result);
        }))
    }, [selectedTask]) 

  return (
    <Paper
      style={{
        minWidth: "1280px",
        minHeight: "400px",
        padding: "8px"
      }}
    >
      <Typography color="textPrimary" variant="h6">
        Details
      </Typography>

      <Grid container spacing={1.5}>
        <Grid>
          <TextField
            key={view.selectedTaskId + "_code"}
            id="task-package-code"
            label="Code"
            margin="normal"
            value={selectedTask.code}
            onChange={(e) => runInAction(() => { selectedTask.code = e.target.value })}
            variant="standard"
          />
        </Grid>
        <Grid>
          <TextField
            key={view.selectedTaskId + "_name"}
            id="task-package-name"
            label="Name"
            margin="normal"
            value={selectedTask.name}
            onChange={(e) => runInAction(() => { selectedTask.name = e.target.value })}
            variant="standard"
          />
        </Grid>
      </Grid>

      <Grid container spacing={1.5} mt={1.5}>
        <Grid>
          <ForecastMethodPicker task={selectedTask} />
        </Grid>
        <Grid>
          <Typography id="discrete-slider" variant="subtitle2">
            Cost/Duration Weight Allocation
          </Typography>
          <Slider
            value={parseInt(selectedTask.getCostWeightAllocation())}
            aria-labelledby="discrete-slider"
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => (
              <div>{value + "/" + (100 - value)}</div>
            )}
            onChange={handleChangeWeightAllocation}
            step={1}
            min={0}
            max={100}
          />
        </Grid>
        <Grid>
        <NumericFormat
                  key={view.selectedTaskId + "_cost"}
                  id="forecast-at-completion"
                  label="Forecast At Completion"
                  value={FACValues[selectedTask.getRealForecastMethod()]}
                  thousandSeparator
                  decimalScale={2}
                  prefix="$"
                  customInput={TextField}
                  variant="standard"
                  slotProps={{
                    input: {
                      readOnly: true
                    }
                  }}
                />
                </Grid>
      </Grid>

      <Typography color="textPrimary" variant="h6">
        Plan
      </Typography>

      <Grid container spacing={1.5} sx={{ pointerEvents: "none" }}>
        <Grid>
          <NumericFormat
            key={view.selectedTaskId + "_cost"}
            id="planned-cost"
            label="Planned Cost"
            value={selectedTask.plannedCost}
            thousandSeparator
            decimalScale={2}
            prefix="$"
            customInput={TextField}
            variant="standard"
            slotProps={{
              input: {
                readOnly: true
              }
            }}
          />
        </Grid>
        <Grid>
          <TextField
            type="number"
            key={view.selectedTaskid + "_duration"}
            id="planned-duration"
            label="Planned Duration"
            value={selectedTask.plannedDuration}
            variant="standard"
            slotProps={{
              input: {
                readOnly: true
              },
              inputLabel: {
                shrink: true
              }
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1.5} mt={1.5} sx={{ pointerEvents: "none" }}>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <Grid sx={{ ml: 1, maxWidth: "200px" }}>
            <DatePicker
              format="yyyy-MM-dd"
              id="planned-start-date-picker"
              label="Planned Start Date"
              value={selectedTask.plannedStartDate &&
                DateTime.fromISO(selectedTask.plannedStartDate).isValid
                ? DateTime.fromISO(selectedTask.plannedStartDate)
                : null}
              onChange={(event) => false}
              readOnly
              slotProps={{
                textField: { variant: "standard" },
              }}
            />
          </Grid>
          <Grid>
            <DatePicker
              id="planned-end-date-picker"
              format="yyyy-MM-dd"
              label="Planned End Date"
              value={selectedTask.plannedEndDate &&
                DateTime.fromISO(selectedTask.plannedEndDate).isValid
                ? DateTime.fromISO(selectedTask.plannedEndDate)
                : null}
              onChange={(event) => false}
              readOnly
              slotProps={{
                textField: { variant: "standard" }
              }}
            />
          </Grid>
          <Grid>
            <DatePicker
              format="yyyy-MM-dd"
              id="actual-start-date-picker"
              label="Actual Start Date"
              value={DateTime.fromISO(selectedTask.actualStartDate).isValid
                ? DateTime.fromISO(selectedTask.actualStartDate)
                : null}
              onChange={(e) => false}
              readonly
              slotProps={{
                textField: { variant: "standard" }
              }}
            />

          </Grid>
        </LocalizationProvider>
      </Grid>

      <Grid container mt={2} spacing={1.5} sx={{ pointerEvents: "none" }}>
        <TextField
          key={view.selectedTaskId + "_plannedStartPeriod"}
          id="planned-start-period"
          label="Start Period"
          value={selectedTask.plannedStartPeriod}
          variant="standard"
          slotProps={{
            input: {
              readOnly: true
            }
          }}
        />
        <TextField
          key={view.selectedTaskId + "_plannedEndPeriod"}
          id="planned-end-period"
          label="End Period"
          value={selectedTask.plannedEndPeriod}
          variant="standard"
          slotProps={{
            input: {
              readOnly: true
            }
          }}
        />
        <TextField
          key={view.selectedTaskId + "_forecastedEndPeriod"}
          id="forecasted-end-period"
          label="Forecasted End Period"
          value={selectedTask.forecastedEndPeriod}
          variant="standard"
          slotProps={{
            input: {
              readOnly: true
            }
          }}
        />
      </Grid>

      <Typography color="textPrimary" variant="h6">
        Distribution
      </Typography>
      <TaskDistributionPicker task={selectedTask} />

      <hr />
      <ButtonGroup variant="text">
        <Button color="primary" onClick={() => view.setTaskViewSelectedTab("actual")}>Actuals</Button>
        <Button color="primary" onClick={() => view.setTaskViewSelectedTab("analysis")}>Analysis</Button>
        <Button color="primary" onClick={() => view.setTaskViewSelectedTab("baseline")}>Baselines</Button>
      </ButtonGroup>

      <Box sx={{ width: "600px" }}>
        <View
          task={selectedTask}
          tab={view.taskViewSelectedTab}
        />
      </Box>
    </Paper>
  )
}

// class TaskPackagePanel extends React.Component {
//   constructor(props) {
//     super(props);
//     this.state = {
//       selectedTab: "actual",
//     };
//   }

//   handleAnalysis = () => {
//     this.setState({ selectedTab: "analysis" });
//   };

//   handleActual = () => {
//     this.setState({ selectedTab: "actual" });
//   };

//   handleBaseline = () => {
//     this.setState({ selectedTab: "baseline" });
//   };

//   handleChangeWeightAllocation = (event, newValue) => {
//     this.props.selectedTask.costWeightAllocation = newValue.toString();
//     this.props.selectedTask.durationWeightAllocation = (
//       100 - newValue
//     ).toString();
//   };

//   handleChangeTaskPackageName = (event) => {
//     this.props.selectedTask.name = event.target.value;
//   };

//   handleChangeTaskPackageCode = (event) => {
//     this.props.selectedTask.code = event.target.value;
//   };

//   render() {
//     return (
//       <Paper style={{ minWidth: "800px", minHeight: "400px", padding: "8px" }}>
//         <Typography color="textPrimary" variant="h6">
//           Details
//         </Typography>

//         <div>
//           <Grid container>
//             <TextField
//               key={this.props.view.selectedTaskId + "_code"}
//               id="taskPackageCode"
//               label="Code"
//               className={this.props.classes.textField}
//               margin="normal"
//               value={this.props.selectedTask.code}
//               InputLabelProps={{
//                 shrink: true,
//               }}
//               onChange={this.handleChangeTaskPackageCode}
//             />
//             <ForecastMethodPicker task={this.props.selectedTask} />
//           </Grid>
//           <Grid container>
//             <Grid item>
//               <TextField
//                 key={this.props.view.selectedTaskId + "_name"}
//                 id="taskName"
//                 label="Task Package Name"
//                 className={this.props.classes.textField}
//                 margin="normal"
//                 value={this.props.selectedTask.name}
//                 InputLabelProps={{
//                   shrink: true,
//                 }}
//                 onChange={this.handleChangeTaskPackageName}
//               />
//             </Grid>
//             <Grid style={{ width: "200px", margin: "19px" }} item>
//               <Grid container>
//                 <Typography id="discrete-slider" variant="subtitle2">
//                   Cost/Duration Weight Allocation
//                 </Typography>

//                 <Slider
//                   value={parseInt(
//                     this.props.selectedTask.getCostWeightAllocation()
//                   )}
//                   aria-labelledby="discrete-slider"
//                   valueLabelDisplay="auto"
//                   valueLabelFormat={(value) => (
//                     <div>{value + "/" + (100 - value)}</div>
//                   )}
//                   onChange={this.handleChangeWeightAllocation}
//                   step={1}
//                   min={0}
//                   max={100}
//                 />
//               </Grid>
//             </Grid>
//           </Grid>
//         </div>
//         <Typography color="textPrimary" variant="h6">
//           Plan
//         </Typography>
//         <div style={{ pointerEvents: 'none' }}>
//           <NumericFormat
//             readOnly={true}
//             key={this.props.view.selectedTaskId + "_cost"}
//             id="plannedCost"
//             label="Planned Cost"
//             className={this.props.classes.textField}
//             InputLabelProps={{ shrink: true }}
//             value={this.props.selectedTask.plannedCost}
//             thousandSeparator
//             decimalScale={2}
//             prefix="$"
//             customInput={TextField}
//           />
//           <TextField
//             key={this.props.view.selectedTaskId + "_duration"}
//             id="plannedDuration"
//             label="Planned Duration"
//             className={this.props.classes.textField}
//             InputLabelProps={{ shrink: true }}
//             readOnly={true}
//             value={this.props.selectedTask.plannedDuration}
//           />
//         </div>
//         <div style={{ pointerEvents: 'none' }}>
//           <LocalizationProvider dateAdapter={AdapterLuxon}>
//             <DatePicker
//               readOnly={true}
//               disableToolbar
//               variant="inline"
//               format="MM/dd/yyyy"
//               margin="normal"
//               id="planned-start-date-picker"
//               label="Planned Start Date"
//               value={this.props.selectedTask.plannedStartDate}
//               onChange={(event) => false}
//               KeyboardButtonProps={{
//                 "aria-label": "change date",
//               }}
//             />
//             <DatePicker
//               readOnly={true}
//               disableToolbar
//               variant="inline"
//               format="MM/dd/yyyy"
//               margin="normal"
//               id="planned-end-date-picker"
//               label="Planned End Date"
//               value={this.props.selectedTask.plannedEndDate}
//               onChange={(event) => false}
//               KeyboardButtonProps={{
//                 "aria-label": "change date",
//               }}
//             />
//           </LocalizationProvider>
//         </div>
//         <div style={{ pointerEvents: 'none' }}>
//           <TextField
//             key={this.props.view.selectedTaskId + "_plannedStartPeriod"}
//             id="plannedStartPeriod"
//             label="Start Period"
//             className={this.props.classes.textField}
//             InputLabelProps={{ shrink: true }}
//             readOnly={true}
//             value={this.props.selectedTask.plannedStartPeriod}
//           />
//           <TextField
//             key={this.props.view.selectedTaskId + "_plannedEndPeriod"}
//             id="plannedEndPeriod"
//             label="End Period"
//             className={this.props.classes.textField}
//             InputLabelProps={{ shrink: true }}
//             readOnly={true}
//             value={this.props.selectedTask.plannedEndPeriod}
//           />
//           <TextField
//             readOnly={true}
//             key={this.props.view.selectedTaskId + "_forecastedEndPeriod"}
//             id="forecastedEndPeriod"
//             label="Forecasted End Period"
//             className={this.props.classes.textField}
//             InputLabelProps={{ shrink: true }}
//             value={this.props.selectedTask.forecastedEndPeriod}
//           />
//         </div>
//         <Typography color="textPrimary" variant="h6">
//           Distribution
//         </Typography>
//         <div>
//           <TaskDistributionPicker task={this.props.selectedTask} />
//         </div>
//         <hr />
//         <div>
//           <ButtonGroup variant="text" color="primary">
//             <Button onClick={() => this.handleActual()}>Actuals</Button>
//             <Button onClick={() => this.handleAnalysis()}>Analysis</Button>
//             <Button onClick={() => this.handleBaseline()}>Baselines</Button>
//           </ButtonGroup>
//         </div>
//         <div width="600px">
//           <View
//             task={this.props.selectedTask}
//             view={this.state.selectedTab}
//             classes={this.props.classes}
//           />
//         </div>
//       </Paper>
//     );
//   }
// }

// TaskPackagePanel.propTypes = {
//   classes: PropTypes.object.isRequired,
// };

export default observer(TaskPackagePanel);
